import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Separator } from "@/shared/components/ui/separator";
import { Badge } from "@/shared/components/ui/badge";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  User,
  CalendarDays,
  DollarSign,
  Package,
  Tag,
  FileText,
  Shirt,
  CreditCard,
} from "lucide-react";

import { RentalType, InvoicePaymentMethod } from "@/shared/types/enums";
import {
  formatCurrency,
  formatDate,
  formatPhone,
} from "@/shared/lib/formatters";
import { formatInstallments } from "@/features/rentals/constants";
import type { RentalResponseDTO } from "@/shared/types/rental.types";
import type { TagResponseDTO } from "@/features/tags/types";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import { RentalInvoicesSection } from "@/features/invoices/components/RentalInvoicesSection";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { RentalStatusBadge } from "../../components/RentalStatusBadge";

interface RentalDetailDialogProps {
  open: boolean;
  rental: RentalResponseDTO | null;
  tagsMap: Map<number, TagResponseDTO>;
  getProductById: (
    id: number,
    signal?: AbortSignal,
  ) => Promise<ProductResponseDTO | null>;
  onClose: () => void;
}

const PAYMENT_LABELS: Record<InvoicePaymentMethod, string> = {
  [InvoicePaymentMethod.CASH]: "Dinheiro",
  [InvoicePaymentMethod.CREDIT_CARD]: "Cartão de crédito",
  [InvoicePaymentMethod.DEBIT_CARD]: "Cartão de débito",
  [InvoicePaymentMethod.PIX]: "PIX",
  [InvoicePaymentMethod.BANK_TRANSFER]: "Transferência bancária",
};

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div>
    <p className="text-xs" style={{ color: "#a1a1aa" }}>
      {label}
    </p>
    <p className="text-sm font-medium" style={{ color: "#e5e7eb" }}>
      {value || "—"}
    </p>
  </div>
);

export function RentalDetailDialog({
  open,
  rental,
  tagsMap,
  getProductById,
  onClose,
}: RentalDetailDialogProps) {
  const [productDetails, setProductDetails] = useState<
    Map<number, ProductResponseDTO>
  >(new Map());
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (
      !open ||
      !rental ||
      rental.rentalType !== RentalType.GOWN ||
      !rental.gownItems?.length
    ) {
      // Limpa detalhes quando dialog fecha ou rental muda para não-GOWN.
      // Sincronização com props externas (open/rental) — legítimo.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProductDetails(new Map());
      return;
    }
    const productIds = [...new Set(rental.gownItems.map((g) => g.productId))];
    const controller = new AbortController();
    setLoadingProducts(true);
    Promise.all(productIds.map((id) => getProductById(id, controller.signal)))
      .then((results) => {
        if (controller.signal.aborted) return;
        const map = new Map<number, ProductResponseDTO>();
        results.forEach((p) => {
          if (p) map.set(p.id, p);
        });
        setProductDetails(map);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingProducts(false);
      });
    return () => controller.abort();
  }, [open, rental, getProductById]);

  const remaining = rental ? rental.totalValue - rental.paidValue : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: "#09090b",
          border: "1px solid #27272a",
          borderRadius: "0.75rem",
        }}
      >
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle style={{ color: "#f4f4f5", fontSize: "1.25rem" }}>
                Contrato {rental?.contractNumber}
              </DialogTitle>
              <DialogDescription style={{ color: "#a1a1aa" }}>
                Aluguel #{rental?.id} ·{" "}
                {rental?.rentalType === RentalType.SUIT ? "Terno" : "Beca"}
              </DialogDescription>
            </div>
            {rental && (
              <RentalStatusBadge
                status={rental.status}
                expectedInAt={rental.expectedInAt}
              />
            )}
          </div>
        </DialogHeader>

        {rental && (
          <div className="space-y-5">
            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <div
                className="flex items-center gap-2 text-sm font-medium"
                style={{ color: "#a1a1aa" }}
              >
                <User className="w-4 h-4" /> Informações principais
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <InfoRow label="Cliente" value={rental.clientName} />
                <InfoRow
                  label="Telefone"
                  value={
                    rental.clientPhone ? formatPhone(rental.clientPhone) : "—"
                  }
                />
                <InfoRow label="Vendedor" value={rental.sellerName} />
                <InfoRow
                  label="Criado em"
                  value={formatDate(rental.rentedAt)}
                />
              </div>
            </div>

            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <div
                className="flex items-center gap-2 text-sm font-medium"
                style={{ color: "#a1a1aa" }}
              >
                <CalendarDays className="w-4 h-4" /> Datas
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <InfoRow
                  label="Saída prevista"
                  value={formatDate(rental.expectedOutAt)}
                />
                <InfoRow
                  label="Retorno previsto"
                  value={formatDate(rental.expectedInAt)}
                />
                <InfoRow
                  label="Saída real"
                  value={rental.outAt ? formatDate(rental.outAt) : "—"}
                />
                <InfoRow
                  label="Retorno real"
                  value={rental.inAt ? formatDate(rental.inAt) : "—"}
                />
              </div>
            </div>

            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <div
                className="flex items-center gap-2 text-sm font-medium"
                style={{ color: "#a1a1aa" }}
              >
                <DollarSign className="w-4 h-4" /> Valores
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs" style={{ color: "#a1a1aa" }}>
                    Total
                  </p>
                  <p className="text-lg font-bold" style={{ color: "#3b82f6" }}>
                    {formatCurrency(rental.totalValue)}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#a1a1aa" }}>
                    Pago
                  </p>
                  <p className="text-lg font-bold" style={{ color: "#22c55e" }}>
                    {formatCurrency(rental.paidValue)}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#a1a1aa" }}>
                    Restante
                  </p>
                  <p
                    className="text-lg font-bold"
                    style={{ color: remaining > 0 ? "#f97316" : "#22c55e" }}
                  >
                    {formatCurrency(remaining)}
                  </p>
                </div>
              </div>
            </div>

            {rental.payments && rental.payments.length > 0 && (
              <div
                className="rounded-lg p-4 space-y-3"
                style={{
                  backgroundColor: "#18181b",
                  border: "1px solid #27272a",
                }}
              >
                <div
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: "#a1a1aa" }}
                >
                  <CreditCard className="w-4 h-4" /> Pagamentos realizados
                </div>
                <ul className="space-y-1.5">
                  {rental.payments.map((p, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between px-3 py-2 rounded text-sm"
                      style={{ backgroundColor: "#09090b" }}
                    >
                      <span style={{ color: "#a1a1aa" }}>
                        {PAYMENT_LABELS[p.paymentMethod] ?? p.paymentMethod}
                        {formatInstallments(p.installments)
                          ? ` · ${formatInstallments(p.installments)}`
                          : ""}
                      </span>
                      <span
                        className="font-medium"
                        style={{ color: "#22c55e" }}
                      >
                        {formatCurrency(p.paidValue)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Can permission={ACTIONS.invoices.read}>
              <RentalInvoicesSection rentalId={rental.id} />
            </Can>

            {rental.rentalType === RentalType.SUIT && (
              <div
                className="rounded-lg p-4 space-y-3"
                style={{
                  backgroundColor: "#18181b",
                  border: "1px solid #27272a",
                }}
              >
                <div
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: "#a1a1aa" }}
                >
                  <Package className="w-4 h-4" /> Peças (
                  {rental.stockItems?.length ?? 0})
                  {rental.hasShoes && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      + {rental.shoesPairQuantity} par(es) sapato
                    </Badge>
                  )}
                </div>
                {rental.stockItems?.length > 0 ? (
                  <ul className="space-y-1.5">
                    {rental.stockItems.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2 rounded text-sm"
                        style={{ backgroundColor: "#09090b" }}
                      >
                        <Shirt
                          className="w-3.5 h-3.5 shrink-0"
                          style={{ color: "#a1a1aa" }}
                        />
                        <span style={{ color: "#e5e7eb" }}>
                          <span className="font-medium">{s.productName}</span>
                          <span style={{ color: "#a1a1aa" }} className="ml-2">
                            #{s.id} · {s.barcode} · Tam. {s.size}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm" style={{ color: "#a1a1aa" }}>
                    Nenhuma peça associada
                  </p>
                )}
              </div>
            )}

            {rental.rentalType === RentalType.GOWN &&
              rental.gownItems?.length > 0 && (
                <div
                  className="rounded-lg p-4 space-y-3"
                  style={{
                    backgroundColor: "#18181b",
                    border: "1px solid #27272a",
                  }}
                >
                  <div
                    className="flex items-center gap-2 text-sm font-medium"
                    style={{ color: "#a1a1aa" }}
                  >
                    <Package className="w-4 h-4" /> Itens de beca (
                    {rental.gownItems.length})
                  </div>
                  {loadingProducts ? (
                    <div
                      className="flex items-center gap-2 py-2"
                      style={{ color: "#a1a1aa" }}
                    >
                      <Spinner className="w-4 h-4" />
                      <span className="text-sm">
                        Carregando detalhes dos produtos...
                      </span>
                    </div>
                  ) : (
                    <ul className="space-y-1.5">
                      {rental.gownItems.map((g) => {
                        const p = productDetails.get(g.productId);
                        return (
                          <li
                            key={g.idGownRentalItem}
                            className="flex items-center justify-between px-3 py-2 rounded text-sm"
                            style={{ backgroundColor: "#09090b" }}
                          >
                            <span style={{ color: "#e5e7eb" }}>
                              {p ? (
                                <>
                                  <span className="font-medium">
                                    {p.name ?? `${p.brand} ${p.color}`}
                                  </span>
                                  <span
                                    style={{ color: "#a1a1aa" }}
                                    className="ml-2"
                                  >
                                    #{p.id} · {p.brand} · {p.color}
                                  </span>
                                </>
                              ) : (
                                <span style={{ color: "#a1a1aa" }}>
                                  Produto #{g.productId}
                                </span>
                              )}
                            </span>
                            <span
                              style={{
                                color:
                                  g.returnedQuantity === g.quantity
                                    ? "#22c55e"
                                    : "#a1a1aa",
                              }}
                            >
                              {g.returnedQuantity}/{g.quantity} devolvido(s)
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

            {rental.tagIds?.length > 0 && (
              <div
                className="rounded-lg p-4 space-y-2"
                style={{
                  backgroundColor: "#18181b",
                  border: "1px solid #27272a",
                }}
              >
                <div
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: "#a1a1aa" }}
                >
                  <Tag className="w-4 h-4" /> Tags
                </div>
                <div className="flex flex-wrap gap-2">
                  {rental.tagIds.map((id) => {
                    const tag = tagsMap.get(id);
                    return tag ? (
                      <Badge
                        key={id}
                        variant="outline"
                        style={{ borderColor: tag.color, color: tag.color }}
                      >
                        {tag.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {rental.description && (
              <>
                <Separator style={{ backgroundColor: "#27272a" }} />
                <div className="space-y-2">
                  <div
                    className="flex items-center gap-2 text-sm font-medium"
                    style={{ color: "#a1a1aa" }}
                  >
                    <FileText className="w-4 h-4" /> Observações
                  </div>
                  <div
                    className="rounded-lg p-3 text-sm"
                    style={{
                      backgroundColor: "#18181b",
                      border: "1px solid #27272a",
                      color: "#e5e7eb",
                    }}
                  >
                    {rental.description}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
